import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarHeart } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { SpecialtyFilter } from "../components/about/SpecialtyFilter";
import { trackBookClick } from "../lib/analytics";
import { CREDENTIALS, TEAM } from "../data/site";

export default function About() {
    const [group, setGroup] = useState(null);
    const { hash } = useLocation();
    useEffect(() => {
        if (!hash) return;
        const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
        return () => clearTimeout(t);
    }, [hash]);
    const members = TEAM.filter((m) => m.bio && (!group || m.groups?.includes(group)));
    return (
        <div data-testid="about-page" className="pb-28">
            <PageHeader
                testid="about-header"
                breadcrumb="Our Story"
                title="Meet Dora & the practice"
                number="01"
                label="Our Story"
                heading="A room where the light stays on for you"
            >
                <div className="mt-12 grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
                    <div className="flex flex-col gap-5">
                        <motion.img
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            src="/images/dora.jpg"
                            alt="Dora Henderson, therapist and author, smiling in a pink beanbag chair"
                            className="w-full rounded-3xl object-cover shadow-[0_20px_60px_rgba(0,0,0,0.45)] ring-1 ring-amber-500/15"
                        />
                        <div>
                            <p className="font-serif text-xl text-amber-100">
                                Dora Henderson
                            </p>
                            <p className="text-sm text-amber-200/60">
                                M.A., LMHC-QS, RPT-S · Founder
                            </p>
                        </div>
                        <div className="relative">
                            <img
                                src="/images/dora-fun.jpg"
                                alt="Dora blowing a giant bubble"
                                className="w-full rounded-2xl object-cover ring-1 ring-amber-500/15"
                            />
                            <span className="mt-2 block font-serif text-sm italic text-amber-400/80">
                                yes, we keep bubbles here
                            </span>
                        </div>
                    </div>

                    <div>
                        <p className="text-base leading-relaxed text-amber-100/75">
                            NeuroPlay Counseling began with a simple belief:
                            healing happens faster in rooms that feel warm. So
                            Dora built one in Longwood, Florida — a cozy office
                            where children lead with play, teens are taken
                            seriously, and adults finally set down what they've
                            been carrying.
                        </p>
                        <p className="mt-5 text-base leading-relaxed text-amber-100/75">
                            Dora Henderson is a Licensed Mental Health Counselor
                            and Registered Play Therapist-Supervisor who has
                            spent her career helping young people name big
                            feelings — in the therapy room, and on the page. Her
                            books and the Neuro & the 'Ception Force Friends
                            characters grew out of the same work: making the
                            invisible world of feelings something children can
                            see, hold, and talk about.
                        </p>

                        <div className="mt-8 space-y-4">
                            {CREDENTIALS.map((c) => (
                                <div
                                    key={c.title}
                                    className="rounded-2xl border border-amber-500/20 bg-[#1F1510]/60 p-6"
                                >
                                    <h3 className="font-serif text-lg text-amber-200">
                                        {c.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-amber-100/60">
                                        {c.body}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-20">
                    <h3 className="font-serif text-2xl tracking-tight text-amber-50 sm:text-3xl">
                        The people who keep the candles lit
                    </h3>
                    <p className="mt-3 max-w-2xl text-base leading-relaxed text-amber-100/60">
                        A small, deeply caring team — each bringing their own warmth,
                        training, and lived experience to the room.
                    </p>
                    <SpecialtyFilter active={group} onChange={setGroup} count={members.length} />
                    <div className="mt-10 flex flex-col gap-8">
                        {members.map((m) => (
                            <motion.div
                                key={m.name}
                                id={`team-${m.name.split(" ")[0].toLowerCase()}`}
                                data-testid={`team-bio-${m.name.split(" ")[0].toLowerCase()}`}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5 }}
                                className="grid scroll-mt-28 gap-6 rounded-3xl border border-amber-500/15 bg-[#1F1510]/50 p-6 sm:p-8 lg:grid-cols-[220px_1fr]"
                            >
                                <div className="flex flex-col items-center lg:items-start">
                                    <img
                                        src={`/images/${m.img}`}
                                        alt={`${m.name}, ${m.role} at NeuroPlay Counseling`}
                                        className="aspect-square w-40 rounded-2xl object-cover ring-1 ring-amber-500/15 lg:w-full"
                                    />
                                    <p className="mt-4 text-center font-serif text-xl text-amber-100 lg:text-left">
                                        {m.name}
                                    </p>
                                    <p className="text-center text-xs text-amber-200/55 lg:text-left">
                                        {m.creds}
                                    </p>
                                    <p className="mt-1 text-center text-sm text-amber-400/80 lg:text-left">
                                        {m.role}
                                    </p>
                                    {m.booking && (
                                        <a
                                            href={m.booking}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => trackBookClick(m.name)}
                                            data-testid={`book-with-${m.name.split(" ")[0].toLowerCase()}`}
                                            className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-semibold text-[#1F1510] transition-[background-color,transform] duration-200 hover:bg-amber-300 active:scale-95"
                                        >
                                            <CalendarHeart className="h-4 w-4" />
                                            Book with {m.name.split(" ")[0]}
                                        </a>
                                    )}
                                </div>

                                <div>
                                    {m.specialties && (
                                        <div className="mb-5 flex flex-wrap gap-2">
                                            {m.specialties.map((s) => (
                                                <span
                                                    key={s}
                                                    className="rounded-full border border-amber-500/25 bg-amber-500/5 px-3 py-1 text-xs text-amber-200/80"
                                                >
                                                    {s}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="space-y-3">
                                        {m.bio.map((para, pi) => (
                                            <p
                                                key={pi}
                                                className="text-sm leading-relaxed text-amber-100/70"
                                            >
                                                {para}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </PageHeader>
        </div>
    );
}
